/**
 * Support Agent Dashboard — Full Featured
 * Assignment, Priority, Internal Notes, Filters
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Headphones, LogOut, RefreshCw, Send, Clock, CheckCircle,
  AlertCircle, XCircle, ArrowLeft, ChevronRight, BarChart3,
  Inbox, RotateCcw, UserCheck, AlertTriangle, ArrowUp,
  StickyNote, ChevronDown
} from 'lucide-react';
import { AttachmentViewer } from '../components/AttachmentViewer';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const priorityConfig = {
  low: { color: 'bg-slate-500/20 text-slate-300 border-slate-500/30', label: 'Low', sort: 0 },
  medium: { color: 'bg-blue-500/20 text-blue-300 border-blue-500/30', label: 'Medium', sort: 1 },
  high: { color: 'bg-orange-500/20 text-orange-300 border-orange-500/30', label: 'High', sort: 2 },
  urgent: { color: 'bg-red-500/20 text-red-300 border-red-500/30', label: 'Urgent', sort: 3 }
};

const statusConfig = {
  open: { color: 'bg-amber-500/20 text-amber-300', icon: AlertCircle, label: 'Open' },
  in_progress: { color: 'bg-blue-500/20 text-blue-300', icon: Clock, label: 'In Progress' },
  closed: { color: 'bg-emerald-500/20 text-emerald-300', icon: CheckCircle, label: 'Closed' }
};

const SupportDashboard = () => {
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [view, setView] = useState('list');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignFilter, setAssignFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [changingPriority, setChangingPriority] = useState(false);

  const getToken = () => localStorage.getItem('supportToken');
  const getHeaders = () => ({ 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' });
  const authOnly = () => ({ 'Authorization': `Bearer ${getToken()}` });

  useEffect(() => {
    const token = getToken();
    if (!token) { navigate('/support'); return; }
    fetch(`${API_BASE}/api/support/verify`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(async r => {
        if (r.ok) { const d = await r.json(); setAgent(d.agent); }
        else { localStorage.removeItem('supportToken'); navigate('/support'); }
      })
      .catch(() => { localStorage.removeItem('supportToken'); navigate('/support'); });
  }, [navigate]);

  const fetchTickets = useCallback(async () => {
    const token = getToken(); if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (statusFilter !== 'all') params.set('status_filter', statusFilter);
      if (assignFilter !== 'all') params.set('assignment', assignFilter);
      if (priorityFilter !== 'all') params.set('priority', priorityFilter);
      const res = await fetch(`${API_BASE}/api/support/tickets?${params}`, { headers: authOnly() });
      if (res.ok) setTickets((await res.json()).tickets);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [statusFilter, assignFilter, priorityFilter]);

  const fetchStats = useCallback(async () => {
    const token = getToken(); if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/support/stats`, { headers: authOnly() });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const openTicket = async (ticketOrId) => {
    const id = typeof ticketOrId === 'string' ? ticketOrId : ticketOrId.id;
    setView('detail'); setDetailLoading(true); setReplyText(''); setNoteText(''); setShowNotes(false);
    try {
      const res = await fetch(`${API_BASE}/api/support/tickets/${id}`, { headers: authOnly() });
      if (res.ok) setSelectedTicket(await res.json());
    } catch (e) { toast.error('Failed to load'); }
    finally { setDetailLoading(false); }
  };

  const refreshDetail = () => { if (selectedTicket) openTicket(selectedTicket.id); };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setReplying(true);
    try {
      const res = await fetch(`${API_BASE}/api/support/tickets/${selectedTicket.id}/reply`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ message: replyText })
      });
      if (res.ok) { toast.success('Reply sent'); setReplyText(''); refreshDetail(); fetchTickets(); fetchStats(); }
    } catch (e) { toast.error('Failed'); }
    finally { setReplying(false); }
  };

  const handleClose = async () => {
    if (!selectedTicket) return;
    try {
      const res = await fetch(`${API_BASE}/api/support/tickets/${selectedTicket.id}/close`, { method: 'POST', headers: getHeaders() });
      if (res.ok) { toast.success('Ticket closed'); refreshDetail(); fetchTickets(); fetchStats(); }
    } catch (e) { toast.error('Failed'); }
  };

  const handleReopen = async () => {
    if (!selectedTicket) return;
    try {
      const res = await fetch(`${API_BASE}/api/support/tickets/${selectedTicket.id}/reopen`, { method: 'POST', headers: getHeaders() });
      if (res.ok) { toast.success('Ticket reopened'); refreshDetail(); fetchTickets(); fetchStats(); }
    } catch (e) { toast.error('Failed'); }
  };

  const handleAssign = async () => {
    if (!selectedTicket) return;
    try {
      const res = await fetch(`${API_BASE}/api/support/tickets/${selectedTicket.id}/assign`, { method: 'POST', headers: getHeaders() });
      if (res.ok) { toast.success('Assigned to you'); refreshDetail(); fetchTickets(); fetchStats(); }
    } catch (e) { toast.error('Failed'); }
  };

  const handleUnassign = async () => {
    if (!selectedTicket) return;
    try {
      const res = await fetch(`${API_BASE}/api/support/tickets/${selectedTicket.id}/unassign`, { method: 'POST', headers: getHeaders() });
      if (res.ok) { toast.success('Unassigned'); refreshDetail(); fetchTickets(); fetchStats(); }
    } catch (e) { toast.error('Failed'); }
  };

  const handleChangePriority = async (newPriority) => {
    if (!selectedTicket) return;
    setChangingPriority(true);
    try {
      const res = await fetch(`${API_BASE}/api/support/tickets/${selectedTicket.id}/priority?priority=${newPriority}`, { method: 'POST', headers: getHeaders() });
      if (res.ok) { toast.success(`Priority: ${newPriority}`); refreshDetail(); fetchTickets(); }
    } catch (e) { toast.error('Failed'); }
    finally { setChangingPriority(false); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !selectedTicket) return;
    setAddingNote(true);
    try {
      const res = await fetch(`${API_BASE}/api/support/tickets/${selectedTicket.id}/note`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ note: noteText })
      });
      if (res.ok) { toast.success('Note added'); setNoteText(''); refreshDetail(); }
    } catch (e) { toast.error('Failed'); }
    finally { setAddingNote(false); }
  };

  const handleLogout = () => { localStorage.removeItem('supportToken'); localStorage.removeItem('supportAgent'); toast.success('Logged out'); navigate('/support'); };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800" data-testid="support-dashboard">
      <header className="bg-white/5 backdrop-blur-xl border-b border-white/10 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {view === 'detail' && <Button variant="ghost" size="icon" onClick={() => { setView('list'); setSelectedTicket(null); }} className="text-white"><ArrowLeft className="w-5 h-5" /></Button>}
            <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center">
              <Headphones className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Support Dashboard</h1>
              <p className="text-xs text-slate-400">{agent?.name || 'Agent'}</p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="ghost" className="text-slate-400 hover:text-white"><LogOut className="w-4 h-4 mr-2" />Logout</Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats - Clickable */}
        {stats && view === 'list' && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { label: 'My Tickets', value: stats.my_tickets, cls: 'from-teal-500/20 to-teal-600/10 border-teal-500/20', txt: 'text-teal-300', ic: 'text-teal-400', icon: UserCheck, filter: () => { setAssignFilter('mine'); setStatusFilter('all'); setPriorityFilter('all'); } },
              { label: 'Unassigned', value: stats.unassigned, cls: 'from-amber-500/20 to-amber-600/10 border-amber-500/20', txt: 'text-amber-300', ic: 'text-amber-400', icon: Inbox, filter: () => { setAssignFilter('unassigned'); setStatusFilter('all'); setPriorityFilter('all'); } },
              { label: 'Open', value: stats.open, cls: 'from-amber-500/20 to-amber-600/10 border-amber-500/20', txt: 'text-amber-300', ic: 'text-amber-400', icon: AlertCircle, filter: () => { setAssignFilter('all'); setStatusFilter('open'); setPriorityFilter('all'); } },
              { label: 'In Progress', value: stats.in_progress, cls: 'from-blue-500/20 to-blue-600/10 border-blue-500/20', txt: 'text-blue-300', ic: 'text-blue-400', icon: Clock, filter: () => { setAssignFilter('all'); setStatusFilter('in_progress'); setPriorityFilter('all'); } },
              { label: 'Urgent', value: stats.urgent, cls: 'from-red-500/20 to-red-600/10 border-red-500/20', txt: 'text-red-300', ic: 'text-red-400', icon: AlertTriangle, filter: () => { setAssignFilter('all'); setStatusFilter('all'); setPriorityFilter('urgent'); } },
              { label: 'Closed', value: stats.closed, cls: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/20', txt: 'text-emerald-300', ic: 'text-emerald-400', icon: CheckCircle, filter: () => { setAssignFilter('all'); setStatusFilter('closed'); setPriorityFilter('all'); } },
              { label: 'Total', value: stats.total, cls: 'from-purple-500/20 to-purple-600/10 border-purple-500/20', txt: 'text-purple-300', ic: 'text-purple-400', icon: BarChart3, filter: () => { setAssignFilter('all'); setStatusFilter('all'); setPriorityFilter('all'); } },
            ].map((s, i) => (
              <Card key={i} className={`bg-gradient-to-br ${s.cls} backdrop-blur border cursor-pointer hover:scale-[1.03] transition-transform`} onClick={s.filter}>
                <CardContent className="p-3 flex items-center justify-between">
                  <div><p className={`text-xs ${s.txt}`}>{s.label}</p><p className="text-xl font-bold text-white">{s.value}</p></div>
                  <s.icon className={`w-6 h-6 ${s.ic}`} />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Ticket List */}
        {view === 'list' && (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
                {['all', 'mine', 'unassigned'].map(f => (
                  <button key={f} onClick={() => setAssignFilter(prev => prev === f && f !== 'all' ? 'all' : f)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${assignFilter === f ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/20' : 'text-slate-400 hover:bg-white/10'}`}>
                    {f === 'all' ? 'All' : f === 'mine' ? 'My Tickets' : 'Unassigned'}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
                {['all', 'open', 'in_progress', 'closed'].map(f => (
                  <button key={f} onClick={() => setStatusFilter(prev => prev === f && f !== 'all' ? 'all' : f)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${statusFilter === f ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/20' : 'text-slate-400 hover:bg-white/10'}`}>
                    {f === 'all' ? 'All Status' : f === 'in_progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
                {['all', 'urgent', 'high', 'medium', 'low'].map(f => (
                  <button key={f} onClick={() => setPriorityFilter(prev => prev === f && f !== 'all' ? 'all' : f)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${priorityFilter === f ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20' : 'text-slate-400 hover:bg-white/10'}`}>
                    {f === 'all' ? 'All Priority' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
              {(assignFilter !== 'all' || statusFilter !== 'all' || priorityFilter !== 'all') && (
                <button onClick={() => { setAssignFilter('all'); setStatusFilter('all'); setPriorityFilter('all'); }}
                  className="px-3 py-1.5 rounded-md text-xs font-medium bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-all flex items-center gap-1">
                  <XCircle className="w-3 h-3" /> Reset Filters
                </button>
              )}
              <Button variant="outline" size="icon" onClick={() => { fetchTickets(); fetchStats(); }} className="border-white/20 text-white ml-auto">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            {/* Active filter indicator */}
            {(assignFilter !== 'all' || statusFilter !== 'all' || priorityFilter !== 'all') && (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>Showing:</span>
                {assignFilter !== 'all' && <Badge className="bg-teal-500/20 text-teal-300">{assignFilter === 'mine' ? 'My Tickets' : 'Unassigned'}</Badge>}
                {statusFilter !== 'all' && <Badge className="bg-blue-500/20 text-blue-300">{statusFilter === 'in_progress' ? 'In Progress' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}</Badge>}
                {priorityFilter !== 'all' && <Badge className="bg-orange-500/20 text-orange-300">{priorityFilter.charAt(0).toUpperCase() + priorityFilter.slice(1)} Priority</Badge>}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-teal-500 animate-spin" /></div>
            ) : tickets.length === 0 ? (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="py-16 text-center">
                  <Inbox className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400 mb-3">
                    {assignFilter !== 'all' || statusFilter !== 'all' || priorityFilter !== 'all'
                      ? 'No tickets match the selected filters'
                      : 'No tickets found'}
                  </p>
                  {(assignFilter !== 'all' || statusFilter !== 'all' || priorityFilter !== 'all') && (
                    <Button size="sm" onClick={() => { setAssignFilter('all'); setStatusFilter('all'); setPriorityFilter('all'); }}
                      className="bg-teal-500 hover:bg-teal-600 text-white">
                      Show All Tickets
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {tickets.map(t => {
                  const sc = statusConfig[t.status] || statusConfig.open;
                  const pc = priorityConfig[t.priority] || priorityConfig.medium;
                  const isAssignedToMe = t.assigned_to?.agent_id === agent?.id;
                  return (
                    <Card key={t.id} className={`bg-white/5 border-white/10 hover:bg-white/10 transition-all cursor-pointer ${t.priority === 'urgent' ? 'border-l-2 border-l-red-500' : t.priority === 'high' ? 'border-l-2 border-l-orange-500' : ''}`}
                      onClick={() => openTicket(t)} data-testid={`agent-ticket-${t.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-xs font-mono text-slate-500">#{t.ticket_no}</span>
                              <Badge className={sc.color}><sc.icon className="w-3 h-3 mr-1" />{sc.label}</Badge>
                              <Badge className={pc.color}>{pc.label}</Badge>
                              <Badge variant="outline" className="text-slate-400 text-xs border-white/20">{t.category}</Badge>
                              {t.last_reply_by === 'student' && t.status !== 'closed' && <Badge className="bg-red-500/20 text-red-300 text-xs animate-pulse">Needs Reply</Badge>}
                              {isAssignedToMe && <Badge className="bg-teal-500/20 text-teal-300 text-xs">Mine</Badge>}
                            </div>
                            <h3 className="font-semibold text-white truncate">{t.subject}</h3>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                              <span className="text-slate-400">{t.user_name || t.user_email}</span>
                              <span>{formatDate(t.updated_at)}</span>
                              <span>{t.messages_count} msg</span>
                              {t.assigned_to && <span className="text-teal-400">Assigned: {t.assigned_to.agent_name} ({t.assigned_to.department || 'Support'})</span>}
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-600 flex-shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Ticket Detail */}
        {view === 'detail' && (
          <div className="space-y-4">
            {detailLoading ? (
              <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-teal-500 animate-spin" /></div>
            ) : selectedTicket?.messages ? (
              <>
                {/* Ticket Header */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader className="pb-3">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between flex-wrap gap-2">
                        <div>
                          <span className="text-sm font-mono text-slate-500">Ticket #{selectedTicket.ticket_no}</span>
                          <CardTitle className="text-white mt-1">{selectedTicket.subject}</CardTitle>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <Badge className={(statusConfig[selectedTicket.status] || statusConfig.open).color}>
                              {(statusConfig[selectedTicket.status] || statusConfig.open).label}
                            </Badge>
                            <Badge className={(priorityConfig[selectedTicket.priority] || priorityConfig.medium).color}>
                              {(priorityConfig[selectedTicket.priority] || priorityConfig.medium).label}
                            </Badge>
                            <Badge variant="outline" className="text-slate-400 border-white/20">{selectedTicket.category}</Badge>
                            <span className="text-xs text-slate-400 font-medium">{selectedTicket.user_name || 'Student'}</span>
                            <span className="text-xs text-slate-600">{selectedTicket.user_email}</span>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {selectedTicket.status !== 'closed' ? (
                            <>
                              {!selectedTicket.assigned_to ? (
                                <Button size="sm" onClick={handleAssign} className="bg-teal-500/20 hover:bg-teal-500/30 text-teal-300" data-testid="assign-btn">
                                  <UserCheck className="w-4 h-4 mr-1" />Assign to me
                                </Button>
                              ) : selectedTicket.assigned_to.agent_id === agent?.id ? (
                                <>
                                  <Button size="sm" onClick={handleUnassign} variant="outline" className="border-white/20 text-slate-300" data-testid="unassign-btn">Unassign</Button>
                                  <Button size="sm" onClick={handleClose} className="bg-red-500/20 hover:bg-red-500/30 text-red-300" data-testid="close-ticket-btn">
                                    <XCircle className="w-4 h-4 mr-1" />Close
                                  </Button>
                                </>
                              ) : (
                                <Badge className="bg-teal-500/20 text-teal-300 py-1.5">Assigned: {selectedTicket.assigned_to.agent_name} ({selectedTicket.assigned_to.department || 'Support'})</Badge>
                              )}
                            </>
                          ) : (
                            <Button size="sm" onClick={handleReopen} className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300" data-testid="reopen-ticket-btn">
                              <RotateCcw className="w-4 h-4 mr-1" />Reopen
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Priority Changer */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">Priority:</span>
                        {['low', 'medium', 'high', 'urgent'].map(p => (
                          <button key={p} onClick={() => handleChangePriority(p)} disabled={changingPriority}
                            className={`px-2 py-0.5 rounded text-xs font-medium transition-all ${selectedTicket.priority === p ? (priorityConfig[p].color + ' ring-1 ring-white/20') : 'bg-white/5 text-slate-500 hover:bg-white/10'}`}>
                            {p.charAt(0).toUpperCase() + p.slice(1)}
                          </button>
                        ))}
                      </div>

                      {/* Assigned To */}
                      {selectedTicket.assigned_to && (
                        <div className="text-xs text-slate-500">
                          Handling: <span className="text-teal-400 font-medium">{selectedTicket.assigned_to.agent_name} ({selectedTicket.assigned_to.department || 'Support'})</span>
                          <span className="ml-2">since {formatDate(selectedTicket.assigned_to.assigned_at)}</span>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                </Card>

                {/* Internal Notes Toggle */}
                <button onClick={() => setShowNotes(!showNotes)}
                  className="flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300 transition-colors" data-testid="toggle-notes">
                  <StickyNote className="w-4 h-4" />Internal Notes ({selectedTicket.internal_notes?.length || 0})
                  <ChevronDown className={`w-4 h-4 transition-transform ${showNotes ? 'rotate-180' : ''}`} />
                </button>
                {showNotes && (
                  <Card className="bg-amber-500/5 border-amber-500/20">
                    <CardContent className="p-3 space-y-2">
                      {selectedTicket.internal_notes?.length > 0 ? (
                        selectedTicket.internal_notes.map((n, i) => (
                          <div key={i} className="bg-white/5 rounded-lg p-2 text-sm">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-amber-400 font-medium text-xs">{n.agent_name}</span>
                              <span className="text-slate-500 text-xs">{formatDate(n.timestamp)}</span>
                            </div>
                            <p className="text-slate-300 whitespace-pre-wrap">{n.note}</p>
                          </div>
                        ))
                      ) : <p className="text-slate-500 text-sm">No internal notes yet</p>}
                      <div className="flex gap-2 mt-2">
                        <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add internal note (not visible to student)..."
                          rows={2} className="flex-1 bg-white/10 border border-amber-500/20 rounded-lg px-3 py-2 text-white resize-none text-sm placeholder-slate-500" data-testid="internal-note-input" />
                        <Button onClick={handleAddNote} disabled={addingNote || !noteText.trim()} className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 self-end" data-testid="add-note-btn">
                          <StickyNote className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Messages */}
                <div className="space-y-3">
                  {selectedTicket.messages.map((m, i) => (
                    <div key={i} className={`flex ${m.by === 'student' ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        m.by === 'student'
                          ? 'bg-white/10 border border-white/10 text-white rounded-bl-md'
                          : 'bg-gradient-to-r from-teal-500/90 to-cyan-500/90 text-white rounded-br-md'
                      }`}>
                        <div className={`text-xs mb-1 font-medium ${m.by === 'student' ? 'text-amber-400' : 'text-teal-100'}`}>
                          {m.by === 'student' ? `${m.sender_name || 'Student'}` : `${m.sender_name || 'You'} (${m.display_name || 'Support'})`}
                        </div>
                        {m.message && <p className="text-sm whitespace-pre-wrap">{m.message}</p>}
                        <AttachmentViewer
                          attachments={m.attachments}
                          tokenHeader={getHeaders()}
                          endpoint="support"
                          theme="dark"
                        />
                        <p className={`text-xs mt-1 ${m.by === 'student' ? 'text-slate-500' : 'text-teal-200'}`}>{formatDate(m.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reply */}
                {selectedTicket.status !== 'closed' && (
                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="p-3">
                      <div className="flex gap-2">
                        <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type your reply to student..."
                          rows={3} className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white resize-none text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 placeholder-slate-500" data-testid="agent-reply-input" />
                        <Button onClick={handleReply} disabled={replying || !replyText.trim()} className="bg-teal-500 hover:bg-teal-600 text-white self-end" data-testid="agent-reply-btn">
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : <p className="text-slate-400 text-center">Ticket not found</p>}
          </div>
        )}
      </main>
    </div>
  );
};

export default SupportDashboard;
